import { HttpClient } from '@angular/common/http';
import { computed, effect, inject, untracked } from '@angular/core';
import { Router } from '@angular/router';

import { AppError } from '@hglobal/fin-ui';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { map, of, switchMap } from 'rxjs';

import { AccessRightsResponse, parseAccessRoleList, parseRoleFromApi, Role, Team, TeamStatus } from '@zeusm/business/core/entities';
import { buildBusinessApiUrl, buildBusinessApiUrlV2 } from '@zeusm/shared/core/helpers';
import { ProfileService } from '@zeusm/shared/session/profile';
import { setErrorStatusFor, setPendingStatusFor, setSuccessStatusFor, withRequestStatus } from '@zeusm/shared/store';

const ORG_ID_FOR_ALL_ORGS = -1;

interface StorageState {
  team: Team;
  readonlyRights: boolean;
  teams: Team[] | null;
  activeTeams: Team[] | null;
  accessRights: AccessRightsResponse | null;
  orgId: string | null;
}

const initialState: StorageState = {
  readonlyRights: false,
  teams: null,
  activeTeams: null,
  team: null,
  accessRights: null,
  orgId: null,
};

export const StorageStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withRequestStatus(['loadTeams']),
  withProps(() => ({
    profileService: inject(ProfileService),
  })),
  withComputed(store => ({
    role: computed(() => (store.accessRights()?.role ? parseRoleFromApi(store.accessRights().role) : null)),
    permissions: computed(() => {
      const rights = store.accessRights();
      return rights?.permissions ? new Map(rights.permissions.map(item => [item.module, item.actions])) : null;
    }),
    teamIdsWithNames: computed(() => store.activeTeams()?.map(org => ({ name: org.name, id: org.orgId })) || []),
    canInvite: computed(() => parseAccessRoleList(store.accessRights()?.canInvite)),
    canResendInvite: computed(() => parseAccessRoleList(store.accessRights()?.canResendInvite)),
    canAdd: computed(() => parseAccessRoleList(store.accessRights()?.canAdd)),
    canDelete: computed(() => parseAccessRoleList(store.accessRights()?.canDelete)),
    canEdit: computed(() => parseAccessRoleList(store.accessRights()?.canEdit)),
    canSetRole: computed(() => parseAccessRoleList(store.accessRights()?.canSetRole)),
    canChangeStatus: computed(() => parseAccessRoleList(store.accessRights()?.canChangeStatus)),
    canView: computed(() => parseAccessRoleList(store.accessRights()?.canView)),
    manuallySelectedOrgId: computed(() => {
      const role = store.accessRights()?.role ? parseRoleFromApi(store.accessRights().role) : null;
      return role === Role.SuperAdmin ? ORG_ID_FOR_ALL_ORGS : store.orgId();
    }),
  })),
  withMethods(store => ({
    readonlyRightsSet(readonlyRights: boolean) {
      patchState(store, { readonlyRights });
    },
  })),
  withMethods(store => {
    const http = inject(HttpClient);
    const router = inject(Router);
    const NO_ORGANIZATIONS_ERROR_CODE = '136';

    return {
      loadTeams: rxMethod<void>(
        switchMap(() => {
          patchState(store, setPendingStatusFor('loadTeams'));
          return http.get<{ organizations: Team[] }>(buildBusinessApiUrlV2('organizations/')).pipe(
            map(res => res.organizations),
            tapResponse({
              next: teams => {
                const activeTeams = teams.filter(org => org.status === TeamStatus.Active);
                const team = resolveTeam(teams, store.profileService.selectedBusinessProfile()?.externalOrganizationId);
                const orgId = team?.orgId || null;

                patchState(store, { activeTeams, teams, team, orgId }, setSuccessStatusFor('loadTeams'));
              },
              error: (error: AppError) => {
                if (error.code?.toString() === NO_ORGANIZATIONS_ERROR_CODE) {
                  router.navigate(['/no-team']);
                }
                patchState(store, setErrorStatusFor('loadTeams', error));
              },
            }),
          );
        }),
      ),

      loadAccessRights: rxMethod<void>(
        switchMap(() => {
          if (store.accessRights()) {
            return of(store.accessRights());
          }

          return http.get<AccessRightsResponse>(buildBusinessApiUrl('permissions/access')).pipe(
            tapResponse({
              next: accessRights => patchState(store, { accessRights }),
              error: () => {},
            }),
          );
        }),
      ),

      onLogout(): void {
        patchState(store, {
          readonlyRights: false,
          teams: null,
          activeTeams: null,
          team: null,
          accessRights: null,
          orgId: null,
        });
      },
    };
  }),
  withHooks({
    onInit(store) {
      let loadedBusinessProfileId: string | null = null;

      effect(() => {
        const isBusinessProfile = store.profileService.isBusinessProfileActive();
        const selectedBusinessProfile = store.profileService.selectedBusinessProfile();
        const profileId = selectedBusinessProfile?.id;
        if (!isBusinessProfile || !profileId) {
          return;
        }

        untracked(() => {
          if (loadedBusinessProfileId === profileId && store.teams() !== null) {
            return;
          }

          loadedBusinessProfileId = profileId;

          const teams = store.teams();
          if (teams) {
            const team = resolveTeam(teams, selectedBusinessProfile?.externalOrganizationId);
            patchState(store, {
              team,
              orgId: team?.orgId || null,
              accessRights: null,
            });
            store.loadAccessRights();
            return;
          }

          patchState(store, { accessRights: null });
          store.loadAccessRights();
          store.loadTeams();
        });
      });
    },
  }),
);

function resolveTeam(teams: Team[], externalOrganizationId: string | null | undefined): Team | null {
  if (!externalOrganizationId || !teams.length) {
    return null;
  }

  return teams.find(org => org.orgId === externalOrganizationId) ?? null;
}
